import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Lock, Loader2, Eye, EyeOff, Check, X, CheckCircle, XCircle, Coffee } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";

// Common weak passwords to block
const COMMON_PASSWORDS = [
  "123456", "password", "12345678", "qwerty", "123456789", 
  "12345", "1234", "111111", "1234567", "dragon", 
  "123123", "baseball", "iloveyou", "trustno1", "sunshine",
  "password1", "password123", "welcome", "shadow", "ashley"
];

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        await api.verifyResetToken(token);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  // Password validation rules
  const passwordRules = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    };
  }, [formData.password]);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    const rules = Object.values(passwordRules);
    const passedRules = rules.filter(Boolean).length;
    
    if (formData.password.length === 0) return { level: 0, label: "", color: "" };
    if (passedRules <= 2) return { level: 1, label: "Zayıf", color: "bg-red-500" };
    if (passedRules <= 4) return { level: 2, label: "Orta", color: "bg-yellow-500" };
    return { level: 3, label: "Güçlü", color: "bg-green-500" };
  }, [passwordRules, formData.password]);

  // Check if passwords match
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  // Check if all required rules pass
  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allRulesPassed) {
      toast.error("Şifre tüm güvenlik kurallarını karşılamalı");
      return;
    }

    if (!passwordsMatch) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }

    setLoading(true);

    try {
      await api.resetPassword(token, formData.password);
      setSuccess(true);
      toast.success("Şifreniz başarıyla güncellendi!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Şifre sıfırlama başarısız");
    } finally {
      setLoading(false);
    }
  };

  const RuleItem = ({ passed, text }) => (
    <div className={`flex items-center gap-2 text-xs ${passed ? "text-green-400" : "text-[#64748B]"}`}>
      {passed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      <span>{text}</span>
    </div>
  );

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF] mx-auto mb-4" />
          <p className="text-[#B1C1C1]">Bağlantı doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8" data-testid="reset-password-invalid">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex flex-col items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg" 
                alt="Kahve LİGİ Logo"
                className="w-20 h-20 rounded-xl object-contain"
              />
              <span className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider gold-gradient-text">
                Kahve LİGİ
              </span>
            </Link>
          </div>

          <div className="glass-card p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-2">
              Geçersiz Bağlantı
            </h2>
            <p className="text-sm text-[#B1C1C1] mb-6">
              Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş. Lütfen yeni bir bağlantı isteyin.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold">
                Yeni Bağlantı İste
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8" data-testid="reset-password-success">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex flex-col items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg" 
                alt="Kahve LİGİ Logo"
                className="w-20 h-20 rounded-xl object-contain"
              />
              <span className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider gold-gradient-text">
                Kahve LİGİ
              </span>
            </Link>
          </div>

          <div className="glass-card p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-2">
              Şifre Güncellendi
            </h2>
            <p className="text-sm text-[#B1C1C1] mb-6">
              Şifreniz başarıyla güncellendi. Artık yeni şifrenizle giriş yapabilirsiniz.
            </p>
            <Link to="/login">
              <Button className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold">
                Giriş Yap
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg" 
              alt="Kahve LİGİ Logo"
              className="w-20 h-20 rounded-xl object-contain"
            />
            <span className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider gold-gradient-text">
              Kahve LİGİ
            </span>
          </Link>
        </div>

        {/* Motivational Quote */}
        <div className="text-center mb-6 p-3 rounded-lg bg-[#C6A87C]/10 border border-[#C6A87C]/20">
          <p className="text-sm text-[#C6A87C] italic flex items-center justify-center gap-2">
            <Coffee className="w-4 h-4" />
            "Yeni bir başlangıç için her zaman bir fırsat vardır."
          </p>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white text-center mb-2">
            Yeni Şifre Belirle
          </h2>
          <p className="text-sm text-[#B1C1C1] text-center mb-6">
            Güçlü bir şifre seçerek hesabını güvende tut.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-[#B1C1C1]">Yeni Şifre</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-[#050E2E]/50 border-white/10 text-white h-11"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.password.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.level / 3) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.level === 1 ? "text-red-400" :
                      passwordStrength.level === 2 ? "text-yellow-400" :
                      passwordStrength.level === 3 ? "text-green-400" : ""
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>

                  {/* Password Rules */}
                  <div className="grid grid-cols-2 gap-1 p-2 rounded-lg bg-white/5">
                    <RuleItem passed={passwordRules.minLength} text="En az 8 karakter" />
                    <RuleItem passed={passwordRules.hasUppercase} text="Büyük harf (A-Z)" />
                    <RuleItem passed={passwordRules.hasLowercase} text="Küçük harf (a-z)" />
                    <RuleItem passed={passwordRules.hasNumber} text="Rakam (0-9)" />
                    <RuleItem passed={passwordRules.hasSpecial} text="Özel karakter (!@#$)" />
                    <RuleItem passed={passwordRules.notCommon} text="Yaygın şifre değil" />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword" className="text-[#B1C1C1]">Şifre Tekrar</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`pl-10 pr-10 bg-[#050E2E]/50 border-white/10 text-white h-11 ${
                    formData.confirmPassword.length > 0 && !passwordsMatch ? "border-red-500" : ""
                  }`}
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                  <X className="w-3 h-3" />
                  Şifreler eşleşmiyor
                </p>
              )}
              {passwordsMatch && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Şifreler eşleşiyor
                </p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || !allRulesPassed || !passwordsMatch}
              className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold h-11 mt-4"
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                "Şifreyi Güncelle"
              )}
            </Button>
          </form>

          <Link 
            to="/login" 
            className="flex items-center justify-center gap-2 text-sm text-[#4DFFFF] hover:underline mt-4"
          >
            Giriş sayfasına dön
          </Link>
        </div>
      </div>
    </div>
  );
}
